import { settingRowStyle as rowStyle, settingLabelColumnStyle as labelColumnStyle, settingLabelStyle as labelStyle, settingsDescriptionStyle as descriptionStyle, settingOrgIndicatorStyle as orgIndicatorStyle } from './settingsStyles';
// ----- Phase 5.X-settings-schema-ui — Slider row -----
//
// Numeric slider for one schema entry (V1 has only one: confidenceThreshold,
// 75-99% step 1). Renders label + description + range input + live readout
// of the current value with the schema's `unit` suffix.

import type { CSSProperties, ReactElement } from 'react';
import type { SettingsSchemaEntry } from '@/lib/settings-schema';

const sliderColumnStyle: CSSProperties = {
  flex: '0 1 220px',
  minWidth: 0,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 4,
};

const readoutStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--accent, #2563eb)',
  textAlign: 'right',
};

const rangeBoundsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 10,
  color: 'var(--text-muted, #6b7280)',
};

export interface SettingSliderRowProps {
  readonly entry: SettingsSchemaEntry; // must be controlType === 'slider'
  readonly value: number;
  readonly showOrgLock: boolean;
  readonly disabled: boolean;
  readonly disabledReason?: string;
  readonly onChange: (next: number) => void;
}

export function SettingSliderRow({
  entry,
  value,
  showOrgLock,
  disabled,
  disabledReason,
  onChange,
}: SettingSliderRowProps): ReactElement {
  const min = entry.min ?? 0;
  const max = entry.max ?? 100;
  const step = entry.step ?? 1;
  const unit = entry.unit ?? '';

  return (
    <div
      style={rowStyle}
      data-testid={`setting-row-${entry.key}`}
      data-setting-level={entry.level}
    >
      <div style={labelColumnStyle}>
        <div style={labelStyle}>
          {showOrgLock && (
            <span aria-label="Organization compliance control" title="Organization compliance control">
              🔒
            </span>
          )}
          <span>{entry.label}</span>
        </div>
        <div style={descriptionStyle}>{entry.description}</div>
        {showOrgLock && (
          <div style={orgIndicatorStyle}>Organization compliance control</div>
        )}
      </div>
      <div style={sliderColumnStyle}>
        <div style={readoutStyle} data-testid={`setting-readout-${entry.key}`}>
          {value}
          {unit}
        </div>
        <input
          type="range"
          aria-label={entry.label}
          data-testid={`setting-slider-${entry.key}`}
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          title={disabled ? disabledReason : undefined}
          style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
        />
        <div style={rangeBoundsStyle}>
          <span>{min}{unit}</span>
          <span>{max}{unit}</span>
        </div>
      </div>
    </div>
  );
}
